import * as Yup from "yup";

export interface PasswordRequirements {
  length: boolean;
  types: {
    uppercase: boolean;
    lowercase: boolean;
    digits: boolean;
    special: boolean;
  };
  typesCount: number;
  noUserInfo: boolean;
}

export interface UserData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const MIN_LENGTH = 10;
export const MIN_TYPES = 4;
export const SPECIAL_CHAR_SET = "!\"#$%&'()*+,-./:;<=>?@[\\]^_`{|}~";

const SPECIAL_CHAR_CLASS = SPECIAL_CHAR_SET.split("")
  .map((char) => `\\${char}`)
  .join("");

const SPECIAL_CHARS_REGEX = new RegExp(`[${SPECIAL_CHAR_CLASS}]`);

export const getPasswordRequirements = (
  password: string,
  userData?: UserData
): PasswordRequirements => {
  const length = password.length >= MIN_LENGTH;
  const uppercase = /[A-Z]/.test(password);
  const lowercase = /[a-z]/.test(password);
  const digits = /[0-9]/.test(password);
  const special = SPECIAL_CHARS_REGEX.test(password);

  const typesCount = [uppercase, lowercase, digits, special].filter(
    Boolean
  ).length;

  let noUserInfo = true;
  if (userData && password) {
    const lowerPassword = password.toLowerCase();
    const checks: string[] = [];

    if (userData.firstName && userData.firstName.length >= 3) {
      checks.push(userData.firstName.toLowerCase());
    }
    if (userData.lastName && userData.lastName.length >= 3) {
      checks.push(userData.lastName.toLowerCase());
    }
    if (userData.email) {
      const emailPart = userData.email.split("@")[0];
      if (emailPart.length >= 3) {
        checks.push(emailPart.toLowerCase());
      }
    }

    for (const check of checks) {
      if (lowerPassword.includes(check)) {
        noUserInfo = false;
        break;
      }
    }
  }

  return {
    length,
    types: {
      uppercase,
      lowercase,
      digits,
      special,
    },
    typesCount,
    noUserInfo,
  };
};

export const isPasswordValid = (
  password: string,
  userData?: UserData
): boolean => {
  const reqs = getPasswordRequirements(password, userData);
  return reqs.length && reqs.typesCount === MIN_TYPES && reqs.noUserInfo;
};

export const passwordYupValidation = (fieldMapping?: {
  firstName?: string;
  lastName?: string;
  email?: string;
}) => {
  return Yup.string().test(
    "password-strength",
    "Password does not meet complexity requirements",
    function (value) {
      if (!value) return true; // Allow empty (handled by required)

      const firstName = fieldMapping?.firstName
        ? this.parent[fieldMapping.firstName]
        : undefined;
      const lastName = fieldMapping?.lastName
        ? this.parent[fieldMapping.lastName]
        : undefined;
      const email = fieldMapping?.email
        ? this.parent[fieldMapping.email]
        : undefined;

      return isPasswordValid(value, { firstName, lastName, email });
    }
  );
};
